import * as Select from '@radix-ui/react-select'
import { InputHTMLAttributes } from 'react'
import { Ref, forwardRef } from 'react'
import { useController, useFormContext } from 'react-hook-form'

import SvgIcon from '@/components/svg-icon/svgIcon'
import { Typography } from '@/components/typography/typography'
import { cn } from '@/libs/tw'
import { translate } from '@/utils/translator'

import { FormValues } from '../../schema'

type FormInputProps = InputHTMLAttributes<HTMLInputElement> & {
	name: keyof FormValues
	label: string
	error?: string
	required?: boolean
	topOptions?: {
		value: string | undefined
		label: string
	}[]
	options: {
		value: string | undefined
		label: string
	}[]
}

export const FormDropdownInput = ({
	name,
	label,
	options,
	topOptions,
	error,
	required,
	disabled,
}: FormInputProps) => {
	const { control } = useFormContext<FormValues>()

	const { field } = useController({
		name,
		control,
	})

	return (
		<label className={cn('flex flex-col gap-8')}>
			<Typography style={'p'} element="p">
				{`${label} ${required ? '*' : ''}`}
			</Typography>
			<div className="flex flex-col gap-8">
				<Dropdown
					ref={field.ref}
					error={error}
					topOptions={topOptions}
					options={options}
					value={field.value as string}
					disabled={disabled}
					onUpdate={(option) => field.onChange(option)}
				/>
				<div className={cn('h-12 text-danger')}>
					{error && (
						<Typography style={'p-s-bold'} element="p">
							{translate(error)}
						</Typography>
					)}
				</div>
			</div>
		</label>
	)
}

type DropdownProps = {
	value?: string
	error?: string
	topOptions?: {
		value: string | undefined
		label: string
	}[]
	options: {
		value: string | undefined
		label: string
	}[]
	disabled?: boolean
	onUpdate: (value: string) => void
}

const Dropdown = forwardRef(
	(
		{ value, options, error, disabled, topOptions, onUpdate }: DropdownProps,
		forwardedRef?: Ref<HTMLButtonElement>
	) => {
		const getLabel = (value?: string) =>
			[...(topOptions ?? []), ...options].find((o) => o.value === value)?.label

		return (
			<Select.Root onValueChange={(value) => onUpdate(value)}>
				<Select.Trigger
					disabled={disabled}
					ref={forwardedRef}
					className={cn(
						'border-textMobile flex w-full justify-between rounded-8 border p-16 disabled:opacity-75 md:border-userData-text',
						error && '!border-danger'
					)}
				>
					<Select.Value
						placeholder={getLabel(value) ?? translate('guest-data-pls-select')}
					/>
					<SvgIcon
						collectionName="ibe"
						iconName="chevron"
						className="h-24 w-24 rotate-180 text-userData-text"
					/>
				</Select.Trigger>
				<Select.Content
					avoidCollisions={false}
					position="popper"
					side="bottom"
					sideOffset={5}
					className={cn(
						'shadow-none z-10 w-full rounded-8 border border-userData-textMobile bg-userData-bgSecondary md:border-userData-text md:bg-naturel'
					)}
				>
					<Select.Viewport
						className={cn(
							'flex max-h-[500px] max-w-[300px] flex-col overflow-auto md:w-fit'
						)}
					>
						{topOptions && (
							<>
								<Select.Group>
									{topOptions.map((option) => (
										<SelectItem key={option.value} value={option.value}>
											<Typography element="span" style="p" className="py-8">
												{option.label}
											</Typography>
										</SelectItem>
									))}
								</Select.Group>
								<hr className="m-4 bg-userData-textMobile md:bg-userData-text" />
							</>
						)}

						<Select.Group>
							{options.map((option) => (
								<SelectItem key={option.value} value={option.value}>
									<Typography element="span" style="p" className="py-8">
										{option.label}
									</Typography>
								</SelectItem>
							))}
						</Select.Group>
					</Select.Viewport>
				</Select.Content>
			</Select.Root>
		)
	}
)

type SelectItemProps = {
	children: JSX.Element
	value: string | undefined
}

const SelectItem = forwardRef(
	({ children, value }: SelectItemProps, forwardedRef: Ref<HTMLDivElement> | undefined) => (
		<Select.Item
			className={cn('flex cursor-pointer justify-between p-16')}
			value={value ?? ''}
			ref={forwardedRef}
		>
			<Select.ItemText>{children}</Select.ItemText>
			<Select.ItemIndicator className="SelectItemIndicator">
				<SvgIcon
					collectionName="ibe"
					iconName="check"
					className="h-16 w-16 text-userData-text"
				/>
			</Select.ItemIndicator>
		</Select.Item>
	)
)
